import { ApplicationRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { AuthService, LoadingIndicatorService } from "#services/shared";
import { MsalService, MsalBroadcastService } from "@azure/msal-angular";
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { filter, finalize, takeUntil } from "rxjs/operators";
import { LoggedUser } from "#models/logged-user";
import { IUserRole } from "helpers/interfaces/user/user-role.interface";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  loginDisplay = false;
  redirectUrl: string;
  private readonly _destroying$ = new Subject<boolean>();
  // Guards against re-entrant loginRedirect() calls that caused the cold-boot redirect loop.
  private _loginInvoked = false;

  constructor(
    private readonly _authService: AuthService,
    public translate: TranslateService,
    private _msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private readonly _appRef: ApplicationRef,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _loadingIndicatorService: LoadingIndicatorService
  ) {
    translate.addLangs(["en", "ru", "kz"]);
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang("en");
  }

  showDisclaimer: boolean;
  currentUserId: number;

  ngOnInit(): void {
    this.setLoginDisplay();

    const storedRedirectUrl = sessionStorage.getItem("redirectUrl");
    if (storedRedirectUrl) {
      this.redirectUrl = storedRedirectUrl;
    }

    this._msalService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        console.log("msalBroadcastService.msalSubject$");
        if (this._msalService.instance.getAllAccounts().length === 0) {
          // R4: account removed (e.g. signed out elsewhere) — clear stale session and
          // return to login via the router instead of a full-page reload.
          sessionStorage.clear();
          this.setLoginDisplay();
          this._router.navigate(["/login"]);
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        // Only act once MSAL is idle (handleRedirect finished). Set the active
        // account BEFORE any token call, then either load data or start a single login.
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        if (this._authService.isAuthenticated()) {
          this._getUserData();
        } else if (!this._loginInvoked && !this._isRedirectInProgress()) {
          this._loginInvoked = true;
          this._authService.login();
        }
      });

    this._activatedRoute.queryParams
      .pipe(takeUntil(this._destroying$))
      .subscribe((params: Params) => {
        if (params && params["destinationUrl"]) {
          this.redirectUrl = params["destinationUrl"];
          sessionStorage.setItem("redirectUrl", this.redirectUrl);
        }
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(true);
    this._destroying$.complete();
  }

  iAccept() {
    this._authService.acceptDisclaimer(this.currentUserId);
  }
  setLoginDisplay() {
    this.loginDisplay = this._msalService.instance.getAllAccounts().length > 0;
  }

  // Residual-redirect guard (Option A).
  // Context: redirectUri is window.location.origin (see app.module MSALInstanceFactory), so after
  // Entra returns to '/#code=...' the app re-bootstraps, routes '/' -> '/login', and builds a NEW
  // LoginComponent whose instance-level _loginInvoked flag is reset. If inProgress$ emits None while
  // the inbound auth code is still on the URL and getActiveAccount() has not been set yet,
  // isAuthenticated() reads false and a second loginRedirect() fires -> one extra redirect cycle.
  // This guard skips starting a new loginRedirect whenever an inbound code/error fragment is present,
  // letting MSAL's handleRedirectPromise finish so the following None emission resolves as authenticated.
  // Scope of impact: purely defers the *first* login trigger by one MSAL state transition on the
  // redirect-return document. It does NOT change the sign-in method, scopes, tokens, guards, storage,
  // or the app registration. Steady-state (already signed-in) and interactive-login paths are unaffected.
  // Note: if MSAL has already stripped the fragment before None fires, this is a no-op (still safe).
  private _isRedirectInProgress(): boolean {
    const hash = window.location.hash || "";
    return hash.includes("code=") || hash.includes("error=");
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this._msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this._msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this._msalService.instance.getAllAccounts();
      this._msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private _getUserData(): void {
    this._loadingIndicatorService.show();
    this._authService
      .getUserData()
      .pipe(
        finalize(() => {
          this._appRef.tick();
          this._loadingIndicatorService.hide();
        })
      )
      .subscribe(() => {
        const currentUser: LoggedUser = this._authService.getCurrentUser();
        const roles: IUserRole[] = this._authService.getRoles();
        this.currentUserId = currentUser?.ID;
        if (
          !currentUser?.IsDisabled &&
          (roles?.length > 0 || currentUser?.IsSystemAdmin)
        ) {
          sessionStorage.setItem("user", this.currentUserId.toString());
          if (currentUser.DisclaimerAccepted === false) {
            this.showDisclaimer = true;
          } else {
            const redirectUrl =
              sessionStorage.getItem("redirectUrl") || this.redirectUrl;

            if (redirectUrl && redirectUrl !== "") {
              if (
                redirectUrl.includes("admin") &&
                !currentUser?.IsSystemAdmin
              ) {
                this._router.navigate(["/error/not-authorized"]);
                return;
              }
              sessionStorage.removeItem("redirectUrl");
              this._router.navigateByUrl(redirectUrl);
            } else {
              this._router.navigate(["/downtimes"]);
            }
          }
        } else {
          this._router.navigate(["/error/not-authorized"]);
        }
      });
  }
}
